import {
  RadioButton,
  RadioButtonChangeEvent
} from '@progress/kendo-react-inputs';
import React from 'react';
import { PreGame } from '../../constants/settings';
import HelpSupport from '../help-support';
import { PreGameSettings } from '../../types/game-document/settings';

interface PreGameSettingsProps {
  state: PreGameSettings;
  onChange: (name: string, value: boolean) => void;
}

const PreGameSetting = (props: PreGameSettingsProps) => {
  const { state, onChange } = props;
  const [isEnableGameLobby, setIsEnableGameLobby] = React.useState<boolean>(
    state.showPreGameLobby
  );

  const isDisableChatSetting = React.useMemo(() => {
    const chatSetting = [
      state.showPreGameTeamChat,
      state.showPreGameGroupChat,
      state.showPreGameDirectChat,
      state.showPreGameAllPlayerChat,
      state.showPreGameFacilitatorChat
    ];
    return chatSetting.every((c) => c === false);
  }, [
    state.showPreGameTeamChat,
    state.showPreGameGroupChat,
    state.showPreGameDirectChat,
    state.showPreGameAllPlayerChat,
    state.showPreGameFacilitatorChat
  ]);

  const handlePreGameSettingChange = (e: RadioButtonChangeEvent) => {
    const {
      target: { element },
      value
    } = e;
    onChange(element?.name ?? '', value);

    if (e.target.element?.name === PreGame.lobby) {
      setIsEnableGameLobby(value);
    }
  };

  React.useEffect(() => {
    setIsEnableGameLobby(state.showPreGameLobby!);
  }, [state.showPreGameLobby]);

  React.useEffect(() => {
    onChange(PreGame.chats, !isDisableChatSetting);
  }, [isDisableChatSetting]);

  //the logic for this setting is using IsShow, so the 'Show' Radio button will send "true" value, and for the inital value set on false (Hide)
  return (
    <>
      <span className={`d-flex justify-content-end mb-3`}>
        <HelpSupport
          title="Pre Game settings guide"
          url="https://forum.catalystglobal.com/t/4654"
        />
      </span>

      {
        //#region Lobby Section
      }
      <div
        className={'d-flex flex-column gap-1 align-items-start mt-4 mb-2'}
        style={{ borderBottom: '1px solid #dee2e6' }}>
        <h2>Lobby</h2>
      </div>
      <div className={'d-flex gap-1 align-items-center'}>
        <span className={'mr-10 w-30'}>Game Lobby</span>
        <RadioButton
          name={PreGame.lobby}
          value={true}
          label={'Show'}
          checked={state.showPreGameLobby}
          onChange={handlePreGameSettingChange}
        />

        <RadioButton
          name={PreGame.lobby}
          value={false}
          label={'Hide'}
          className={'ml-4'}
          checked={!state.showPreGameLobby}
          onChange={handlePreGameSettingChange}
        />
      </div>
      <div className={'d-flex gap-1 align-items-center mb-2'}>
        <span className={'mb-2 fs-6 text-black-50'}>
          <span className={'material-symbols-outlined fs-6'}>lightbulb</span>
          Visibility of pre-game menu tabs.
        </span>
      </div>

      {
        //#region Player Setup Section
      }
      <div
        className={'d-flex flex-column gap-1 align-items-start mt-4 mb-2'}
        style={{ borderBottom: '1px solid #dee2e6' }}>
        <h2>Player Setup</h2>
      </div>
      <div className={'d-flex gap-1 align-items-center mt-3'}>
        <span className={'mr-10 w-30'}>Change Player Name</span>
        <RadioButton
          name={PreGame.changePlayerName}
          value={true}
          label={'Enable'}
          checked={state.enablePreGameChangePlayerName}
          onChange={handlePreGameSettingChange}
        />

        <RadioButton
          name={PreGame.changePlayerName}
          value={false}
          label={'Disable'}
          className={'ml-4'}
          checked={!state.enablePreGameChangePlayerName}
          onChange={handlePreGameSettingChange}
        />
      </div>
      <div className={'d-flex gap-1 align-items-center mb-2'}>
        <span className={'mb-2 fs-6 text-black-50'}>
          <span className={'material-symbols-outlined fs-6'}>lightbulb</span>
          Enables players to change their name.
        </span>
      </div>
      <div className={'d-flex gap-1 align-items-center mt-3'}>
        <span className={'mr-10 w-30'}>Player Image Join</span>
        <RadioButton
          name={PreGame.playerProfile}
          value={'avatar'}
          label={'Avatar'}
          checked={
            state.profilePreGamePlayerJoin === 'avatar' ||
            !state.profilePreGamePlayerJoin
          }
          onChange={handlePreGameSettingChange}
        />

        <RadioButton
          name={PreGame.playerProfile}
          value={'avatarOrPhoto'}
          label={'Avatar or Photo'}
          className={'ml-4'}
          checked={state.profilePreGamePlayerJoin === 'avatarOrPhoto'}
          onChange={handlePreGameSettingChange}
        />

        <RadioButton
          name={PreGame.playerProfile}
          value={'photo'}
          label={'Photo'}
          className={'ml-4'}
          checked={state.profilePreGamePlayerJoin === 'photo'}
          onChange={handlePreGameSettingChange}
        />
      </div>
      <div className={'d-flex gap-1 align-items-center mb-2'}>
        <span className={'mb-2 fs-6 text-black-50'}>
          <span className={'material-symbols-outlined fs-6'}>lightbulb</span>
          Choose how players are represented when joining the game.
        </span>
      </div>

      {
        //#region Teams Section
      }
      <div
        className={'d-flex flex-column gap-1 align-items-start mt-4 mb-2'}
        style={{ borderBottom: '1px solid #dee2e6' }}>
        <h2>Teams</h2>
      </div>
      <span className={'mb-2 fs-6 text-black-50'}>
        A team consists of <b>multiple devices</b> playing together for a team
        score. <b>One device</b> with many players is not a team.
      </span>
      <div className={'d-flex gap-1 align-items-center mt-2'}>
        <span className={'mr-10 w-30'}>Team Required</span>
        <RadioButton
          name={PreGame.teamRequired}
          value={true}
          label={'Enable'}
          disabled={!isEnableGameLobby}
          checked={state.isPreGameTeamRequired && isEnableGameLobby}
          onChange={handlePreGameSettingChange}
        />

        <RadioButton
          name={PreGame.teamRequired}
          value={false}
          label={'Disable'}
          className={'ml-4'}
          disabled={!isEnableGameLobby}
          checked={!state.isPreGameTeamRequired || !isEnableGameLobby}
          onChange={handlePreGameSettingChange}
        />
      </div>
      <div className={'d-flex gap-1 align-items-center mb-2'}>
        <span className={'mb-2 fs-6 text-black-50'}>
          <span className={'material-symbols-outlined fs-6'}>lightbulb</span>
          Players must join a team to enter the game.
        </span>
      </div>
      <div className={'d-flex gap-1 align-items-center mt-3'}>
        <span className={'mr-10 w-30'}>Teams</span>
        <RadioButton
          name={PreGame.teams}
          value={true}
          label={'Show'}
          disabled={!isEnableGameLobby}
          checked={state.showPreGameTeams && isEnableGameLobby}
          onChange={handlePreGameSettingChange}
        />

        <RadioButton
          name={PreGame.teams}
          value={false}
          label={'Hide'}
          className={'ml-4'}
          disabled={!isEnableGameLobby}
          checked={!state.showPreGameTeams || !isEnableGameLobby}
          onChange={handlePreGameSettingChange}
        />
      </div>
      <div className={'d-flex gap-1 align-items-center mb-2'}>
        <span className={'mb-2 fs-6 text-black-50'}>
          <span className={'material-symbols-outlined fs-6'}>lightbulb</span>
          Visibility of teams tab in menu. Teams tab shows the teams in the game
          <br />
          and each team's members.
        </span>
      </div>
      <div className={'d-flex gap-1 align-items-center mt-3'}>
        <span className={'mr-10 w-30'}>Create teams</span>
        <RadioButton
          name={PreGame.createTeams}
          value={true}
          label={'Enable'}
          disabled={!isEnableGameLobby}
          checked={state.showPreGameCreateTeams && isEnableGameLobby}
          onChange={handlePreGameSettingChange}
        />

        <RadioButton
          name={PreGame.createTeams}
          value={false}
          label={'Disable'}
          className={'ml-4'}
          disabled={!isEnableGameLobby}
          checked={!state.showPreGameCreateTeams || !isEnableGameLobby}
          onChange={handlePreGameSettingChange}
        />
      </div>
      <div className={'d-flex gap-1 align-items-center mb-2'}>
        <span className={'mb-2 fs-6 text-black-50'}>
          <span className={'material-symbols-outlined fs-6'}>lightbulb</span>
          Enables players to create teams. Displayed on team tab.
        </span>
      </div>
      <div className={'d-flex gap-1 align-items-center mt-3'}>
        <span className={'mr-10 w-30'}>Join / leave teams</span>
        <RadioButton
          name={PreGame.joinLeaveTeams}
          value={true}
          label={'Enable'}
          disabled={!isEnableGameLobby}
          checked={state.showPreGameJoinLeaveTeams && isEnableGameLobby}
          onChange={handlePreGameSettingChange}
        />

        <RadioButton
          name={PreGame.joinLeaveTeams}
          value={false}
          label={'Disable'}
          className={'ml-4'}
          disabled={!isEnableGameLobby}
          checked={!state.showPreGameJoinLeaveTeams || !isEnableGameLobby}
          onChange={handlePreGameSettingChange}
        />
      </div>
      <div className={'d-flex gap-1 align-items-center mb-2'}>
        <span className={'mb-2 fs-6 text-black-50'}>
          <span className={'material-symbols-outlined fs-6'}>lightbulb</span>
          Enables players to join or leave a team. Displayed on team tab.
        </span>
      </div>

      {
        //#region Communitation Section
      }
      <div
        className={'d-flex flex-column gap-1 align-items-start mt-4 mb-2'}
        style={{ borderBottom: '1px solid #dee2e6' }}>
        <h2>Communication</h2>
      </div>
      <div className={'d-flex gap-1 align-items-center mt-3'}>
        <span className={'mr-10 w-30'}>Chat</span>
        <RadioButton
          name={PreGame.chats}
          value={true}
          label={'Show'}
          disabled={!isEnableGameLobby || isDisableChatSetting}
          checked={state.showPreGameChat && isEnableGameLobby}
          onChange={handlePreGameSettingChange}
        />

        <RadioButton
          name={PreGame.chats}
          value={false}
          label={'Hide'}
          className={'ml-4'}
          disabled={!isEnableGameLobby || isDisableChatSetting}
          checked={!state.showPreGameChat || !isEnableGameLobby}
          onChange={handlePreGameSettingChange}
        />
      </div>
      <div className={'d-flex gap-1 align-items-center mb-2'}>
        <span className={'mb-2 fs-6 text-black-50'}>
          <span className={'material-symbols-outlined fs-6'}>lightbulb</span>
          Visibility of chat in the pre-game menu.
        </span>
      </div>
      <div className={'d-flex gap-1 align-items-center mt-3'}>
        <span className={'mr-10 w-30'}>Facilitator Chat</span>
        <RadioButton
          name={PreGame.facilitatorChat}
          value={true}
          label={'Enable'}
          disabled={!isEnableGameLobby}
          checked={state.showPreGameFacilitatorChat && isEnableGameLobby}
          onChange={handlePreGameSettingChange}
        />

        <RadioButton
          name={PreGame.facilitatorChat}
          value={false}
          label={'Disable'}
          className={'ml-4'}
          disabled={!isEnableGameLobby}
          checked={!state.showPreGameFacilitatorChat || !isEnableGameLobby}
          onChange={handlePreGameSettingChange}
        />
      </div>
      <div className={'d-flex gap-1 align-items-center mb-2'}>
        <span className={'mb-2 fs-6 text-black-50'}>
          <span className={'material-symbols-outlined fs-6'}>lightbulb</span>
          Enables a player to message the game controller.
        </span>
      </div>
      <div className={'d-flex gap-1 align-items-center mt-3'}>
        <span className={'mr-10 w-30'}>All Player Chat</span>
        <RadioButton
          name={PreGame.allPlayerChat}
          value={true}
          label={'Enable'}
          disabled={!isEnableGameLobby}
          checked={state.showPreGameAllPlayerChat && isEnableGameLobby}
          onChange={handlePreGameSettingChange}
        />

        <RadioButton
          name={PreGame.allPlayerChat}
          value={false}
          label={'Disable'}
          className={'ml-4'}
          disabled={!isEnableGameLobby}
          checked={!state.showPreGameAllPlayerChat || !isEnableGameLobby}
          onChange={handlePreGameSettingChange}
        />
      </div>
      <div className={'d-flex gap-1 align-items-center mb-2'}>
        <span className={'mb-2 fs-6 text-black-50'}>
          <span className={'material-symbols-outlined fs-6'}>lightbulb</span>
          Enables a player to message all players in the game.
        </span>
      </div>
      <div className={'d-flex gap-1 align-items-center mt-3'}>
        <span className={'mr-10 w-30'}>Direct Chat</span>
        <RadioButton
          name={PreGame.directChat}
          value={true}
          label={'Enable'}
          disabled={!isEnableGameLobby}
          checked={state.showPreGameDirectChat && isEnableGameLobby}
          onChange={handlePreGameSettingChange}
        />

        <RadioButton
          name={PreGame.directChat}
          value={false}
          label={'Disable'}
          className={'ml-4'}
          disabled={!isEnableGameLobby}
          checked={!state.showPreGameDirectChat || !isEnableGameLobby}
          onChange={handlePreGameSettingChange}
        />
      </div>
      <div className={'d-flex gap-1 align-items-center mb-2'}>
        <span className={'mb-2 fs-6 text-black-50'}>
          <span className={'material-symbols-outlined fs-6'}>lightbulb</span>
          Enables a player to message another player.
        </span>
      </div>
      <div className={'d-flex gap-1 align-items-center mt-3'}>
        <span className={'mr-10 w-30'}>Team Chat</span>
        <RadioButton
          name={PreGame.teamChat}
          value={true}
          label={'Enable'}
          disabled={!isEnableGameLobby}
          checked={state.showPreGameTeamChat && isEnableGameLobby}
          onChange={handlePreGameSettingChange}
        />

        <RadioButton
          name={PreGame.teamChat}
          value={false}
          label={'Disable'}
          className={'ml-4'}
          disabled={!isEnableGameLobby}
          checked={!state.showPreGameTeamChat || !isEnableGameLobby}
          onChange={handlePreGameSettingChange}
        />
      </div>
      <div className={'d-flex gap-1 align-items-center mb-2'}>
        <span className={'mb-2 fs-6 text-black-50'}>
          <span className={'material-symbols-outlined fs-6'}>lightbulb</span>
          Enables a player to message all players in a team.
        </span>
      </div>
      <div className={'d-flex gap-1 align-items-center mt-3'}>
        <span className={'mr-10 w-30'}>Group Chat</span>
        <RadioButton
          name={PreGame.groupChat}
          value={true}
          label={'Enable'}
          disabled={!isEnableGameLobby}
          checked={state.showPreGameGroupChat && isEnableGameLobby}
          onChange={handlePreGameSettingChange}
        />

        <RadioButton
          name={PreGame.groupChat}
          value={false}
          label={'Disable'}
          className={'ml-4'}
          disabled={!isEnableGameLobby}
          checked={!state.showPreGameGroupChat || !isEnableGameLobby}
          onChange={handlePreGameSettingChange}
        />
      </div>
      <div className={'d-flex gap-1 align-items-center mb-2'}>
        <span className={'mb-2 fs-6 text-black-50'}>
          <span className={'material-symbols-outlined fs-6'}>lightbulb</span>
          Enables a player to message all players in a group.
        </span>
      </div>

      {
        //#region Player Menu Section
      }
      <div
        className={'d-flex flex-column gap-1 align-items-start mt-4 mb-2'}
        style={{ borderBottom: '1px solid #dee2e6' }}>
        <h2>Player Menu</h2>
      </div>
      <div className={'d-flex gap-1 align-items-center mt-3'}>
        <span className={'mr-10 w-30'}>Inventory</span>
        <RadioButton
          name={PreGame.inventory}
          value={true}
          label={'Show'}
          disabled={!isEnableGameLobby}
          checked={state.showPreGameInventory && isEnableGameLobby}
          onChange={handlePreGameSettingChange}
        />

        <RadioButton
          name={PreGame.inventory}
          value={false}
          label={'Hide'}
          className={'ml-4'}
          disabled={!isEnableGameLobby}
          checked={!state.showPreGameInventory || !isEnableGameLobby}
          onChange={handlePreGameSettingChange}
        />
      </div>
      <div className={'d-flex gap-1 align-items-center mb-2'}>
        <span className={'mb-2 fs-6 text-black-50'}>
          <span className={'material-symbols-outlined fs-6'}>lightbulb</span>
          Visibility of inventory in the pre-game menu.
        </span>
      </div>
      <div className={'d-flex gap-1 align-items-center mt-3'}>
        <span className={'mr-10 w-30'}>Titles</span>
        <RadioButton
          name={PreGame.roles}
          value={true}
          label={'Show'}
          disabled={!isEnableGameLobby}
          checked={state.showPreGameRoles && isEnableGameLobby}
          onChange={handlePreGameSettingChange}
        />

        <RadioButton
          name={PreGame.roles}
          value={false}
          label={'Hide'}
          className={'ml-4'}
          disabled={!isEnableGameLobby}
          checked={!state.showPreGameRoles || !isEnableGameLobby}
          onChange={handlePreGameSettingChange}
        />
      </div>
      <div className={'d-flex gap-1 align-items-center mb-2'}>
        <span className={'mb-2 fs-6 text-black-50'}>
          <span className={'material-symbols-outlined fs-6'}>lightbulb</span>
          Visibility of titles in the pre-game menu.
        </span>
      </div>
      <div className={'d-flex gap-1 align-items-center mt-3'}>
        <span className={'mr-10 w-30'}>Scoreboard</span>
        <RadioButton
          name={PreGame.scoreboard}
          value={true}
          label={'Show'}
          disabled={!isEnableGameLobby}
          checked={state.showPreGameScoreboard && isEnableGameLobby}
          onChange={handlePreGameSettingChange}
        />

        <RadioButton
          name={PreGame.scoreboard}
          value={false}
          label={'Hide'}
          className={'ml-4'}
          disabled={!isEnableGameLobby}
          checked={!state.showPreGameScoreboard || !isEnableGameLobby}
          onChange={handlePreGameSettingChange}
        />
      </div>
      <div className={'d-flex gap-1 align-items-center mb-2'}>
        <span className={'mb-2 fs-6 text-black-50'}>
          <span className={'material-symbols-outlined fs-6'}>lightbulb</span>
          Visibility of scoreboard in the pre-game menu.
        </span>
      </div>
      <div className={'d-flex gap-1 align-items-center mt-3'}>
        <span className={'mr-10 w-30'}>On-Demand Task</span>
        <RadioButton
          name={PreGame.onDemandTask}
          value={true}
          label={'Show'}
          disabled={!isEnableGameLobby}
          checked={state.showPreGameOnDemandTask && isEnableGameLobby}
          onChange={handlePreGameSettingChange}
        />

        <RadioButton
          name={PreGame.onDemandTask}
          value={false}
          label={'Hide'}
          className={'ml-4'}
          disabled={!isEnableGameLobby}
          checked={!state.showPreGameOnDemandTask || !isEnableGameLobby}
          onChange={handlePreGameSettingChange}
        />
      </div>
      <div className={'d-flex gap-1 align-items-center mb-2'}>
        <span className={'mb-2 fs-6 text-black-50'}>
          <span className={'material-symbols-outlined fs-6'}>lightbulb</span>
          Visibility of on-demand task tab in the menu. The on-demand tab lists
          tasks that
          <br /> players can complete at any time during the game.
        </span>
      </div>

      {
        //#region Player Interaction Section
      }
      <div
        className={'d-flex flex-column gap-1 align-items-start mt-4 mb-2'}
        style={{ borderBottom: '1px solid #dee2e6' }}>
        <h2>Player Interaction</h2>
      </div>
      <div className={'d-flex gap-1 align-items-center mt-3'}>
        <span className={'mr-10 w-30'}>Trade</span>
        <RadioButton
          name={PreGame.trade}
          value={true}
          label={'Enable'}
          disabled={!isEnableGameLobby}
          checked={state.showPreGameTrade && isEnableGameLobby}
          onChange={handlePreGameSettingChange}
        />

        <RadioButton
          name={PreGame.trade}
          value={false}
          label={'Disable'}
          className={'ml-4'}
          disabled={!isEnableGameLobby}
          checked={!state.showPreGameTrade || !isEnableGameLobby}
          onChange={handlePreGameSettingChange}
        />
      </div>
      <div className={'d-flex gap-1 align-items-center mb-2'}>
        <span className={'mb-2 fs-6 text-black-50'}>
          <span className={'material-symbols-outlined fs-6'}>lightbulb</span>
          Enables a player to trade with another player.
        </span>
      </div>
    </>
  );
};

export default PreGameSetting;
